
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { AdminService } from '../../../application/services/AdminService';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../../context/ToastContext';
import { Plus, Search, Loader2, Edit2, Trash2 } from 'lucide-react';
import EmpresaForm from './EmpresaForm';
import CompanyUsersModal from './CompanyUsersModal';

const EmpresasList = () => {
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingEmpresa, setEditingEmpresa] = useState(null); // Estado para edición
    const [viewingUsersEmpresa, setViewingUsersEmpresa] = useState(null); // Estado para ver usuarios
    
    // Estado para modal de confirmación
    const [actionEmpresa, setActionEmpresa] = useState(null);
    
    const toast = useToast();

    const fetchEmpresas = async () => {
        setLoading(true);
        try {
            const data = await AdminService.getEmpresas();
            setEmpresas(data);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error al cargar empresas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmpresas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredEmpresas = empresas.filter(emp => 
        emp.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.ruc?.includes(searchTerm)
    );

    const handleCreateSuccess = () => {
        setShowModal(false);
        setEditingEmpresa(null);
        toast.success(editingEmpresa ? 'Empresa actualizada exitosamente' : 'Empresa registrada exitosamente');
        fetchEmpresas(); 
    };

    const handleEdit = (empresa) => {
        setEditingEmpresa(empresa);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingEmpresa(null); // Limpiar edición al cerrar
    };

    // Abre el modal de confirmación
    const handleStatusToggle = (empresa) => {
        setActionEmpresa(empresa);
    }

    // Ejecuta la acción tras confirmar en el modal
    const confirmToggleStatus = async () => {
        if (!actionEmpresa) return;

        try {
            const newState = actionEmpresa.estado === 'activa' ? 'suspendida' : 'activa';
            await AdminService.toggleEstadoEmpresa(actionEmpresa.id, newState);
            toast.success(`Empresa ${newState === 'activa' ? 'activada' : 'suspendida'}`);
            fetchEmpresas();
        } catch (error) {
            toast.error(error.toString());
        } finally {
            setActionEmpresa(null);
        }
    };

    return (
        <DashboardLayout title="Gestión de Juntas de Agua">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Juntas de Agua</h1>
                    <p className="text-gray-500 text-sm">Administra las empresas suscritas al servicio.</p>
                </div>
                <Button 
                    onClick={() => {
                        setEditingEmpresa(null);
                        setShowModal(true);
                    }}
                    className="flex items-center"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Junta
                </Button>
            </div>

            {/* Search Bar */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6 flex items-center">
                <Search className="w-5 h-5 text-gray-400 mr-3" />
                <input 
                    type="text" 
                    placeholder="Buscar por nombre o RUC..." 
                    className="flex-1 outline-none text-gray-600 placeholder-gray-400"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold">
                                <th className="px-6 py-2">Empresa</th>
                                <th className="px-6 py-2">Representante</th>
                                <th className="px-6 py-2">Ubicación</th>
                                <th className="px-6 py-2">Estado</th>
                                <th className="px-6 py-2 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                        <div className="flex justify-center items-center">
                                            <Loader2 className="w-6 h-6 animate-spin mr-2" />
                                            Cargando datos...
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredEmpresas.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                        No se encontraron resultados
                                    </td>
                                </tr>
                            ) : (
                                filteredEmpresas.map(emp => (
                                    <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-2">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold mr-2 text-xs">
                                                    {emp.nombre.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 text-sm">{emp.nombre}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-2 text-sm text-gray-600">{emp.representanteLegalNombre || 'Por asignar'}</td>
                                        <td className="px-6 py-2 text-sm text-gray-600">
                                            {emp.canton}, {emp.provincia}
                                        </td>
                                        <td className="px-6 py-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                emp.estado === 'activa' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {emp.estado === 'activa' ? 'Activa' : 'Suspendida'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-2 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => handleEdit(emp)}
                                                    className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                                    title="Editar empresa"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    onClick={() => handleStatusToggle(emp)}
                                                    className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                                    title={emp.estado === 'activa' ? 'Suspender' : 'Activar'}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Registro / Edición */}
             <Modal 
                isOpen={showModal} 
                onClose={handleCloseModal}
                title={editingEmpresa ? "Editar Junta de Agua" : "Registro de Nueva Junta de Agua"}
                size="lg"
            >
                <EmpresaForm 
                    initialData={editingEmpresa}
                    onSuccess={handleCreateSuccess}
                    onCancel={handleCloseModal}
                />
            </Modal>
            
            {/* Modal de Usuarios */}
             <Modal 
                isOpen={!!viewingUsersEmpresa} 
                onClose={() => setViewingUsersEmpresa(null)}
                title="Usuarios Registrados"
                size="lg"
            >
                <CompanyUsersModal empresa={viewingUsersEmpresa} />
            </Modal>

            {/* Modal de Confirmación de Estado */}
            <Modal
                isOpen={!!actionEmpresa}
                onClose={() => setActionEmpresa(null)}
                title={actionEmpresa?.estado === 'activa' ? 'Suspender Servicio' : 'Activar Servicio'}
                size="sm"
                footer={
                    <>
                        <Button 
                            onClick={confirmToggleStatus}
                            className={actionEmpresa?.estado === 'activa' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                        >
                            {actionEmpresa?.estado === 'activa' ? 'Confirmar Suspensión' : 'Confirmar Activación'}
                        </Button>
                        <Button 
                            variant="secondary" 
                            onClick={() => setActionEmpresa(null)}
                        >
                            Cancelar
                        </Button>
                    </>
                }
            >
                <div className="text-center sm:text-left">
                    <p className="text-gray-600">
                        ¿Estás seguro de que deseas 
                        <span className="font-bold"> {actionEmpresa?.estado === 'activa' ? 'suspender' : 'activar'} </span> 
                        el servicio para la junta 
                        <span className="font-bold"> {actionEmpresa?.nombre}</span>?
                    </p>
                    {actionEmpresa?.estado === 'activa' && (
                        <p className="mt-2 text-sm text-red-500">
                            La junta perderá acceso inmediato al sistema.
                        </p>
                    )}
                </div>
            </Modal>
        </DashboardLayout>
    );
};

export default EmpresasList;
